import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import classnames from 'classnames';
import map from 'lodash/map';
import orderBy from 'lodash/orderBy';
import isNumber from 'lodash/isNumber';
import remove from 'lodash/remove';
import reverse from 'lodash/reverse';
import includes from 'lodash/includes';
import filter from 'lodash/filter';
import isEqual from 'lodash/isEqual';
import themeable from 'zignal-landscape/lib/themeable';
import Checkbox from 'zignal-landscape/lib/Checkbox';
import Link from 'zignal-landscape/lib/Link';
import { TABLE, TBODY, TD, TH, THEAD, TR } from 'zignal-landscape/lib/Table';
import SortAscendingIcon from 'zignal-landscape/lib/Icon/SortAscendingIcon';
import SortDescendingIcon from 'zignal-landscape/lib/Icon/SortDescendingIcon';
import SortNoneIcon from 'zignal-landscape/lib/Icon/SortNoneIcon';
import ExternalLinkIcon from 'zignal-landscape/lib/Icon/ExternalLinkIcon';
import colors from 'zignal-landscape/lib/styles/colors.css';
import StoryActions, {
  AddToReadingList,
  Remove
} from 'zignal-widgets/lib/StoryActions';
import {
  removeMentionsFailed,
  removeMentionsSucceeded
} from '../../SyndicatedStoryModal.ducks';
import pluralize from 'pluralize';

// import BulkActionsDropdownWrapper from 'zignal-widgets/lib/BulkActionsDropdownWrapper';
import ActionsDropdownWrapper from 'zignal-widgets/lib/ActionsDropdownWrapper';

import { abbreviateForNewsroom } from '../../../../helpers/numberFormat';
import CustomActionsMenu from './components/ActionsMenu';
import noop from 'lodash/noop';

const _formatDate = ({ timestamp }, timezone) => {
  const storyTime = timezone
    ? moment(timestamp).tz(timezone)
    : moment(timestamp);
  return storyTime.format('DD MMM YYYY - LT');
};

const _formatInfluence = ({
  influence,
  normalized_influence: normalizedInfluence
}) => {
  return `${normalizedInfluence} - ${influence}`;
};

const getStartEndDatesFromNewsroomUrl = (newsroomUrl, timezone) => {
  const MOMENT_FORMAT = 'YYYY-MM-DD+HH:mm:ss';
  const MOMENT_FORMAT_WITH_TIMEZONE = 'MM/DD/YYYY h:mma (z)';
  const MOMENT_FORMAT_WITHOUT_TIMEZONE = 'MM/DD/YYYY h:mma';
  if (!newsroomUrl) {
    return { startDate: '', endDate: '' };
  }

  const startTime = newsroomUrl.match(/start_time=(.*?)\&|$/)[1];
  const endTime = newsroomUrl.match(/end_time=(.*?)\&|$/)[1];
  const startDate = moment
    .tz(startTime, MOMENT_FORMAT, timezone)
    .format(MOMENT_FORMAT_WITHOUT_TIMEZONE);
  const endDate =
    endTime === 'now'
      ? moment.tz(timezone).format(MOMENT_FORMAT_WITH_TIMEZONE)
      : moment
        .tz(endTime, MOMENT_FORMAT, timezone)
        .format(MOMENT_FORMAT_WITH_TIMEZONE);
  return { startDate, endDate };
};

// See `getImpressionTextFromCount` at coffee/view_utils/reading_lists/story_analytics.coffee
const _formatImpression = ({ impression }) => {
  // We trust a 0 (false-y) value if we receive one
  return isNumber(impression) ? abbreviateForNewsroom(impression) : 'Unknown';
};

const _sortList = (storiesList, sortProperty, order) => {
  let newSortedList = orderBy(storiesList, sortProperty, order);
  // Stories with Unknown impression are wrongly sorted. Do it manually.
  if (sortProperty === 'impression') {
    const storiesWithUnknownImpression = remove(
      newSortedList,
      ({ impression }) => !isNumber(impression)
    );
    const storiesWithUnknownImpressionReversed = reverse(
      storiesWithUnknownImpression
    );
    if (order === 'desc') {
      newSortedList = [
        ...newSortedList,
        ...storiesWithUnknownImpressionReversed
      ];
    } else {
      newSortedList = [
        ...storiesWithUnknownImpressionReversed,
        ...newSortedList
      ];
    }
  }
  return newSortedList;
};

class StoriesTable extends React.Component {
  constructor(props) {
    super(props);

    this.handleClickHeader = this.handleClickHeader.bind(this);
    this.handleStoryCheckedChange = this.handleStoryCheckedChange.bind(this);
    this.handleSelectAll = this.handleSelectAll.bind(this);
    this.handleCancel = this.handleCancel.bind(this);

    this.state = {
      sortedStoriesList: props.storiesList,
      lastSortBy: null,
      order: 'asc',
      selectedStoriesIds: []
    };
  }

  componentDidMount() {
    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({
      selectedStoriesIds: this.props.checkedStories.map((e) => {
        return e.id;
      })
    });
  }

  componentDidUpdate(prevProps) {
    const { checkedStories, storiesList } = this.props;
    // Stories list is an empty array and suddenly some data. Length should be enough.
    if (storiesList.length !== prevProps.storiesList.length) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        sortedStoriesList: storiesList
      });
    }
    if (!isEqual(checkedStories, prevProps.checkedStories)) {
      const selectedStoriesIds = checkedStories.map((story) => story.id);
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        selectedStoriesIds
      });
    }
  }

  handleClickHeader(columnName) {
    const propertyColumnMap = {
      mediaOutlet: 'media_outlet',
      date: 'timestamp',
      influence: 'normalized_influence',
      impression: 'impression'
    };
    const sortProperty = propertyColumnMap[columnName];
    this.setState(({ sortedStoriesList, lastSortBy, order }) => {
      const newOrder =
        lastSortBy === columnName && order === 'asc' ? 'desc' : 'asc';
      const newSortedList = _sortList(
        sortedStoriesList,
        sortProperty,
        newOrder
      );
      return {
        sortedStoriesList: newSortedList,
        lastSortBy: columnName,
        order: newOrder
      };
    });
  }

  handleStoryCheckedChange(isSelected, storyId) {
    this.setState(({ selectedStoriesIds }) => {
      const newSelectedStoriesIds = [...selectedStoriesIds];
      const indexOnTheList = selectedStoriesIds.indexOf(storyId);
      const isSelectedOnTheList = indexOnTheList !== -1;

      if (!isSelected && isSelectedOnTheList) {
        newSelectedStoriesIds.splice(indexOnTheList, 1);
      } else if (isSelected && !isSelectedOnTheList) {
        newSelectedStoriesIds.push(storyId);
      }

      const selectedStories = filter(this.state.sortedStoriesList, (item) => {
        return includes(newSelectedStoriesIds, item.id);
      });
      this.props.selectedStories(selectedStories);

      return {
        selectedStoriesIds: newSelectedStoriesIds
      };
    });
  }

  handleSelectAll(isSelected) {
    this.setState(({ sortedStoriesList }) => ({
      selectedStoriesIds: isSelected ? map(sortedStoriesList, 'id') : []
    }));
    this.props.selectedStories(isSelected ? this.state.sortedStoriesList : []);
  }

  handleCancel() {
    this.props.selectedStories([]);
    this.setState({
      selectedStoriesIds: []
    });
  }

  areAllStoriesSelected() {
    const { sortedStoriesList, selectedStoriesIds } = this.state;
    return sortedStoriesList.every(({ id }) => selectedStoriesIds.includes(id));
  }

  render() {
    const {
      styles,
      timezone,
      isAbbreviatedLayoutActionsDropdown,
      isRestrictSearchByDates,
      isDeleting,
      getActionsDropdown,
      onToggleRestriction,
      campaign,
      checkedStories,
      newsroomUrl,
      unshownStories,
      removeMentions
    } = this.props;
    const { sortedStoriesList, lastSortBy, order } = this.state;

    const selectedStoryCount = this.state.selectedStoriesIds.length;

    const iconNames = {
      mediaOutlet: <SortNoneIcon />,
      date: <SortNoneIcon />,
      influence: <SortNoneIcon />,
      impression: <SortNoneIcon />
    };

    iconNames[lastSortBy] =
      order === 'asc' ? <SortAscendingIcon /> : <SortDescendingIcon />;

    const isBulkActionsEnabled = selectedStoryCount > 0;

    const columnsWidth = ['35%', '20%', '15%', '20%', '10%'];
    const getColumnStyleByIndex = (index) => ({ width: columnsWidth[index] });

    const _isStoryChecked = (storyId) => {
      return this.state.selectedStoriesIds.indexOf(storyId) !== -1;
    };
    const { startDate, endDate } = getStartEndDatesFromNewsroomUrl(
      newsroomUrl,
      timezone
    );
    const tableBodyClasses = {
      [styles.tableBodyWrapper]: true
    };
    const showAllText = 'syndicated stories';
    const showByDatesText = `syndicated stories between ${startDate} - ${endDate}`;
    return (
      <div className={styles.StoriesTable}>
        <p className={styles.linkToggleRestrictionWrapper}>
          <span className={styles.linkToggleRestrictionOff}>
            {isRestrictSearchByDates
              ? `Showing ${
                sortedStoriesList ? sortedStoriesList.length : ''
              } ${showByDatesText}. `
              : `Showing all ${
                sortedStoriesList ? sortedStoriesList.length : ''
              } ${showAllText}. `}
          </span>
          <Link onClick={onToggleRestriction}>
            <span className={styles.linkToggleRestriction}>
              {isRestrictSearchByDates
                ? `Show all ${
                  unshownStories ? unshownStories.length : ''
                } ${showAllText}.`
                : `Show ${
                  unshownStories ? unshownStories.length : ''
                } ${showByDatesText} only.`}
            </span>
          </Link>
        </p>
        <div className={styles.tableHeaderWrapper}>
          <TABLE>
            <THEAD>
              <TR>
                <TH style={getColumnStyleByIndex(0)}>
                  <div className={styles.headerContent}>
                    <div
                      className={styles.tableHeaderClickable}
                      onClick={() => this.handleClickHeader('mediaOutlet')}
                    >
                      Media Outlet
                    </div>
                    <div className={styles.iconContent}>
                      {iconNames.mediaOutlet}
                    </div>
                  </div>
                </TH>
                <TH style={getColumnStyleByIndex(1)}>
                  <div className={styles.headerContent}>
                    <div
                      className={styles.tableHeaderClickable}
                      onClick={() => this.handleClickHeader('date')}
                    >
                      Date
                    </div>
                    <div className={styles.iconContent}>{iconNames.date}</div>
                  </div>
                </TH>
                <TH style={getColumnStyleByIndex(2)}>
                  <div className={styles.headerContent}>
                    <div
                      className={styles.tableHeaderClickable}
                      onClick={() => this.handleClickHeader('influence')}
                    >
                      Influence
                    </div>
                    <div className={styles.iconContent}>
                      {iconNames.influence}
                    </div>
                  </div>
                </TH>
                <TH style={getColumnStyleByIndex(3)}>
                  <div className={styles.headerContent}>
                    <div
                      className={styles.tableHeaderClickable}
                      onClick={() => this.handleClickHeader('impression')}
                    >
                      Potential Impressions
                    </div>
                    <div className={styles.iconContent}>
                      {iconNames.impression}
                    </div>
                  </div>
                </TH>
                <TH style={getColumnStyleByIndex(4)}>
                  <span className={styles.tableHeader}>Actions</span>
                </TH>
              </TR>
            </THEAD>
          </TABLE>
        </div>
        <CustomActionsMenu
          selectAllChecked={
            selectedStoryCount === this.state.sortedStoriesList.length
          }
          onSelectAllChange={this.handleSelectAll}
          onCancel={this.handleCancel}
          className={styles.customActionsMenu}
          isVisible={isBulkActionsEnabled}
        >
          <div className={styles.bulkContainer}>
            <span className={styles.bulkMenuCountStories}>
              {`${selectedStoryCount} `}
              {selectedStoryCount === 1 ? 'Story' : 'Stories'} Selected
            </span>
            <StoryActions
              actions={[
                {
                  Component: AddToReadingList,
                  props: {
                    stories: checkedStories,
                    tooltipText: 'Add to Reading List'
                  }
                },
                {
                  Component: Remove,
                  props: {
                    widgetId: newsroomUrl,
                    stories: checkedStories.map((story) => story.id),
                    apiBody: {
                      campaign_id: campaign.id,
                      removals: checkedStories.map((story) => ({
                        created_at: story.created_at,
                        dedup_id: story.dedup_id,
                        story_id: story.id,
                        schema: story.schema
                      }))
                    },
                    isDeleting,
                    onRemoveClicked: removeMentions,
                    tooltipText: `Remove ${pluralize(
                      'Mentions',
                      selectedStoryCount
                    )}`,
                    confirmDialogTitle: `Remove ${pluralize(
                      'Mentions',
                      selectedStoryCount
                    )}`,
                    confirmDialogDescription: this.areAllStoriesSelected() ? (
                      <p>
                        You have selected all the syndicated stories.
                        <br />
                        After these are removed, you will be brought
                        <br />
                        back to the Newsroom.
                      </p>
                    ) : (
                      <p>
                        {/* eslint-disable max-len */}
                        {`${pluralize('This', selectedStoryCount)}`}{' '}
                        {`${pluralize('mentions', selectedStoryCount)}`} will
                        be permanently
                        <br />
                        removed from the entire workspace.
                      </p>
                    ),
                    failureAction: removeMentionsFailed,
                    successAction: removeMentionsSucceeded
                  }
                }
              ]}
              open
              hidden
            />
          </div>
        </CustomActionsMenu>
        <div className={classnames(tableBodyClasses)}>
          <TABLE>
            <TBODY>
              {map(sortedStoriesList, (story) => (
                <TR
                  key={story.id}
                  style={
                    _isStoryChecked(story.id)
                      ? /* v8 ignore next */
                      { background: colors.white01 }
                      : { background: colors.gray01 }
                  }
                >
                  <TD style={getColumnStyleByIndex(0)}>
                    <div className={styles.mediaOutletData}>
                      <div>
                        <span className={styles.checkboxBackgroundVisible}>
                          <Checkbox
                            onChange={(e) => {
                              /* eslint-disable max-len */ /* v8 ignore next */
                              e.target.checked
                                ? e.target
                                  .closest('TR')
                                  .setAttribute(
                                    'style',
                                    'background: white !important'
                                  )
                                : e.target
                                  .closest('TR')
                                  .setAttribute('style', 'background:');
                              this.handleStoryCheckedChange(
                                e.target.checked,
                                story.id
                              );
                            }}
                            checked={_isStoryChecked(story.id)}
                          />
                        </span>
                      </div>
                      <div className={styles.mediaOutletDataLinkAndDomain}>
                        <Link
                          href={story.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <div className={styles.mediaOutletActionLink}>
                            <span className={styles.ellipsis}>
                              {story.media_outlet}
                            </span>
                            <span className={styles.linkIconWrapper}>
                              <ExternalLinkIcon />
                            </span>
                          </div>
                        </Link>
                        <span className={styles.ellipsis375}>
                          ({story.story_domain})
                        </span>
                      </div>
                    </div>
                  </TD>
                  <TD style={getColumnStyleByIndex(1)}>
                    <span className={styles.tableData}>
                      {_formatDate(story, timezone)}
                    </span>
                  </TD>
                  <TD style={getColumnStyleByIndex(2)}>
                    <span className={styles.tableData}>
                      {_formatInfluence(story)}
                    </span>
                  </TD>
                  <TD style={getColumnStyleByIndex(3)}>
                    <span className={styles.tableData}>
                      {_formatImpression(story)}
                    </span>
                  </TD>
                  <TD style={getColumnStyleByIndex(4)}>
                    {/* TODO: enable this and fix React error. */}
                    {/*{isBulkActionsEnabled*/}
                    {/*? <Button disabled iconName="Dropdown" />*/}
                    {/*: <ActionsDropdownWrapper*/}
                    {/*getActionsDropdown={getActionsDropdown}*/}
                    {/*story={story}*/}
                    {/*isAbbreviatedLayout={isAbbreviatedLayoutActionsDropdown}*/}
                    {/*/>*/}
                    {/*}*/}
                    <ActionsDropdownWrapper
                      getActionsDropdown={getActionsDropdown}
                      story={story}
                      isAbbreviatedLayout={isAbbreviatedLayoutActionsDropdown}
                    />
                  </TD>
                </TR>
              ))}
            </TBODY>
          </TABLE>
        </div>
      </div>
    );
  }
}

StoriesTable.propTypes = {
  styles: PropTypes.object.isRequired,
  isRestrictSearchByDates: PropTypes.bool.isRequired,
  getActionsDropdown: PropTypes.func,
  getBulkActionsDropdown: PropTypes.func,
  onToggleRestriction: PropTypes.func,
  addStoryToReadingList: PropTypes.func,
  storiesList: PropTypes.array,
  timezone: PropTypes.string,
  isAbbreviatedLayoutActionsDropdown: PropTypes.bool,
  isDeleting: PropTypes.bool,
  campaign: PropTypes.object.isRequired,
  selectedStories: PropTypes.func,
  removeMentions: PropTypes.func,
  toggleModalVisibility: PropTypes.func,
  checkedStories: PropTypes.array,
  showToolTipAdd: PropTypes.bool,
  changeStateToolTipAdd: PropTypes.func,
  newsroomUrl: PropTypes.string,
  unshownStories: PropTypes.array
};

StoriesTable.defaultProps = {
  storiesList: [],
  checkedStories: [],
  removeMentions: noop
};

export default themeable(StoriesTable);
